import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { NAV_MAIN, NAV_FOOTER } from './navConfig.js';
import NavList from './NavList.jsx';
import { permissions } from '@vanilla/services/PermissionService.js';
import { TooltipProvider } from '@/components/ui/tooltip';
import { UsersApiService } from '@vanilla/services/crm/UsersApiService.js';
import './sidebar.css';

const OPEN_STORAGE_KEY = 'crmNavGroupOpen';

function groupEntries(items) {
    return items.filter((item) => item.children);
}

function isVisible(item, ready) {
    return ready ? item.show?.() !== false : true;
}

function visibleNavItems(items, ready) {
    return items
        .map((item) => item.children
            ? { ...item, children: item.children.filter((c) => isVisible(c, ready)) }
            : item)
        .filter((item) => (item.children ? item.children.length > 0 : isVisible(item, ready)));
}

function loadOpenState(items) {
    let saved = {};
    try {
        saved = JSON.parse(localStorage.getItem(OPEN_STORAGE_KEY) || '{}');
    } catch (err) {
        console.warn('[Sidebar] failed to read nav open state', err);
    }
    const result = {};
    for (const group of groupEntries(items)) {
        result[group.id] = saved[group.id] ?? group.defaultOpen ?? false;
    }
    return result;
}

export default function Sidebar({
    collapsed,
    onToggleCollapse,
    mobileOpen = false,
    onCloseMobile,
    onProfile,
    initials,
    userName,
    currentRoute,
    avatarUrl,
}) {
    const allItems = useMemo(() => [...NAV_MAIN, ...NAV_FOOTER], []);
    const [openMap, setOpenMap] = useState(() => loadOpenState(allItems));
    const [permsReady, setPermsReady] = useState(
        () => permissions.isLoaded() && permissions.getPermissions().length > 0,
    );

    useEffect(() => {
        let active = true;
        const sync = () => {
            if (active) setPermsReady(permissions.isLoaded() && permissions.getPermissions().length > 0);
        };
        permissions.load().catch(() => {}).finally(sync);
        window.addEventListener('authChanged', sync);
        window.addEventListener('navChanged', sync);
        return () => {
            active = false;
            window.removeEventListener('authChanged', sync);
            window.removeEventListener('navChanged', sync);
        };
    }, []);

    useEffect(() => {
        localStorage.setItem(OPEN_STORAGE_KEY, JSON.stringify(openMap));
    }, [openMap]);

    useEffect(() => {
        setOpenMap((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const group of groupEntries(allItems)) {
                if (group.children?.some((c) => c.route === currentRoute) && !next[group.id]) {
                    next[group.id] = true;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [currentRoute, allItems]);

    const toggleGroup = useCallback((id) => {
        setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const handleNavigate = useCallback(() => {
        if (onCloseMobile) {
            onCloseMobile();
        }
    }, [onCloseMobile]);

    const handleProfile = useCallback(() => {
        if (onCloseMobile) {
            onCloseMobile();
        }
        onProfile?.();
    }, [onCloseMobile, onProfile]);

    const mainItems = useMemo(() => visibleNavItems(NAV_MAIN, permsReady), [permsReady]);
    const footerItems = useMemo(() => visibleNavItems(NAV_FOOTER, permsReady), [permsReady]);
    const isNavCollapsed = mobileOpen ? false : collapsed;
    const [avatarBlobUrl, setAvatarBlobUrl] = useState(null);
    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        const load = async () => {
            if (avatarBlobUrl) { try { URL.revokeObjectURL(avatarBlobUrl); } catch {} setAvatarBlobUrl(null); }
            if (!avatarUrl || avatarUrl.startsWith('blob:') || avatarUrl.startsWith('data:')) {
                if (avatarUrl?.startsWith('blob:')) setAvatarBlobUrl(avatarUrl);
                return;
            }
            if (!avatarUrl.includes('/avatar')) return;
            try {
                // Extract userId from /api/v1/users/{id}/avatar
                const m = avatarUrl.match(/\/users\/([^/]+)\/avatar/);
                const uid = m ? decodeURIComponent(m[1]) : null;
                if (!uid) return;
                const res = await UsersApiService.fetchAvatarBlob(uid);
                if (cancelled || !res?.blob) return;
                objectUrl = URL.createObjectURL(res.blob);
                setAvatarBlobUrl(objectUrl);
            } catch {}
        };
        load();
        return () => { cancelled = true; if (objectUrl) try { URL.revokeObjectURL(objectUrl); } catch {} };
    }, [avatarUrl]);

    return (
        <TooltipProvider delayDuration={80}>
            {mobileOpen && (
                <div
                    className="crm-sidebar-backdrop fixed inset-0 bg-black/60 z-[550] md:hidden backdrop-blur-xs transition-opacity duration-200"
                    onClick={onCloseMobile}
                    aria-hidden="true"
                />
            )}
            <aside
                className={`crm-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
                id="crmSidebar"
            >
                <div className="crm-sidebar-header">
                    <Link className="crm-brand" to="/deals" aria-label="Bezohminds CRM home">
                        <img src="/src/assets/bezohminds_logo_transparent.png" alt="" className="crm-brand-logo" width="210" height="65" />
                    </Link>
                    <button
                        type="button"
                        className="crm-nav-toggle"
                        id="crmNavToggle"
                        aria-label={mobileOpen ? 'Close menu' : (collapsed ? 'Expand menu' : 'Collapse menu')}
                        aria-expanded={mobileOpen ? true : !collapsed}
                        onClick={mobileOpen ? onCloseMobile : onToggleCollapse}
                    >
                        {mobileOpen ? (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <path d="M3.5 5.5h13M3.5 10h13M3.5 14.5h13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        )}
                    </button>
                </div>

                <div className="crm-sidebar-body">
                    <nav id="crmNav" className="crm-nav crm-nav-main" aria-label="Main navigation">
                        <NavList
                            items={mainItems}
                            currentRoute={currentRoute}
                            openMap={openMap}
                            onToggleOpen={toggleGroup}
                            onNavigate={handleNavigate}
                            collapsed={isNavCollapsed}
                        />
                    </nav>
                    <div className="crm-sidebar-bottom">
                        <nav id="crmNavFooter" className="crm-nav crm-nav-footer" aria-label="Settings navigation">
                            <NavList
                                items={footerItems}
                                currentRoute={currentRoute}
                                openMap={openMap}
                                onToggleOpen={toggleGroup}
                                onNavigate={handleNavigate}
                                collapsed={isNavCollapsed}
                            />
                        </nav>
                        <div className="crm-sidebar-footer">
                            <button
                                type="button"
                                className="crm-sidebar-profile"
                                id="crmProfileBtn"
                                title="Profile"
                                aria-label="Open profile"
                                aria-expanded="false"
                                aria-haspopup="dialog"
                                data-nav-tooltip="Profile"
                                onClick={handleProfile}
                            >
                                <span className="crm-sidebar-profile-avatar" id="crmProfileBtnAvatar">
                                    {avatarBlobUrl ? (
                                        <img src={avatarBlobUrl} alt="" className="size-full object-cover rounded-full" />
                                    ) : (
                                        <span className="crm-sidebar-profile-initials" id="crmProfileBtnInitials">{initials}</span>
                                    )}
                                </span>
                                <span className="crm-sidebar-profile-text">
                                    <span id="crmUserLabel" className="crm-user-label">{userName}</span>
                                    <span className="crm-sidebar-profile-hint">View profile</span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </aside>
        </TooltipProvider>
    );
}
